/**
 * Cloud saves (docs/design/phone-login-admin.md): which local keys make up a game's save, how a device's copy and the
 * account's copy merge, and one pull → merge → push round. Pure TypeScript over injected storage and client — no DOM,
 * no Phaser — so it runs in Node tests and in the launcher, which syncs every game.
 *
 * Merge rules (never lose progress from another device, spec §12.3):
 * - the map record (`arcade.<id>.map`) is the union of both: furthest road, best stars, skip marks only without stars;
 * - the game's own save (`arcade.<id>`, Mozaik's `mozaik.progress`) comes whole from the side whose road is further,
 *   the local one on a tie (Harf Yılanı has no road: its best results are kept from both sides);
 * - keys a newer version wrote that this one does not know are passed through untouched.
 */
import type { GameId, SaveData, SaveRecord } from "@enjoyment/contracts";
import type { PutSaveResult } from "@enjoyment/platform-client";
import { mergeProgress, normalize, type Progress } from "./progress";

export type { SaveData };

/** The part of PlatformClient a sync needs. */
export interface SaveClient {
  getSaves(): Promise<SaveRecord[] | null>;
  putSave(gameId: GameId, data: SaveData, baseVersion: number): Promise<PutSaveResult>;
}

export interface SaveStorage {
  getItem(key: string): string | null;
  setItem(key: string, value: string): void;
}

export const mapSaveKey = (gameId: string): string => `arcade.${gameId}.map`;

/** The game's own save key: what `loadSave/writeSave` (kit/boot) write, Mozaik's own record. */
export const ownSaveKey = (gameId: string): string => (gameId === "mozaik" ? "mozaik.progress" : `arcade.${gameId}`);

/** The local keys one game's cloud save carries. Settings (mute), analytics ids and attribution never go. */
export function saveKeys(gameId: string): string[] {
  return [ownSaveKey(gameId), mapSaveKey(gameId)];
}

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null && !Array.isArray(value);

function progressOf(value: unknown): Progress | null {
  return isRecord(value) ? normalize(value as Partial<Progress>) : null;
}

/**
 * A save from before the level map has no map record: its level is in the game's own save (`level`; Mozaik's
 * 0-based `levelIndex`), the same reading `loadProgress` migrates once. The launcher syncs games the player has not
 * opened since, so the merge reads it too, or that progress would count as none and lose to another device's.
 */
function legacyMap(gameId: string, data: SaveData): Progress | null {
  const own = data[ownSaveKey(gameId)];
  if (!isRecord(own)) return null;
  const level = gameId === "mozaik" ? (typeof own.levelIndex === "number" ? own.levelIndex + 1 : undefined) : own.level;
  return typeof level === "number" && Number.isFinite(level) ? normalize(null, level) : null;
}

/** A save's map record, or the one its pre-map save stands for. */
function mapOf(gameId: string, data: SaveData): Progress | null {
  return progressOf(data[mapSaveKey(gameId)]) ?? legacyMap(gameId, data);
}

/** How far a save's road goes: the map's open level, 0 without one. */
function reach(gameId: string, data: SaveData): number {
  return mapOf(gameId, data)?.unlocked ?? 0;
}

const num = (value: unknown): number => (typeof value === "number" && Number.isFinite(value) ? value : 0);
const str = (value: unknown): string => (typeof value === "string" ? value : "");

/** Games without a level map keep their best results from both sides instead of choosing one. */
const OWN_SAVE_MERGES: Record<string, (local: Record<string, unknown>, remote: Record<string, unknown>) => Record<string, unknown>> = {
  "harf-yilani": (local, remote) => ({
    ...remote,
    ...local,
    bestScore: Math.max(num(local.bestScore), num(remote.bestScore)),
    bestLength: Math.max(num(local.bestLength), num(remote.bestLength)),
    bestWord: str(remote.bestWord).length > str(local.bestWord).length ? str(remote.bestWord) : str(local.bestWord),
    runs: Math.max(num(local.runs), num(remote.runs)),
  }),
};

/** Merge a game's local save with the account's copy. Pure; neither input is changed. */
export function mergeSaves(gameId: string, local: SaveData, remote: SaveData): SaveData {
  const out: SaveData = {};
  const known = new Set(saveKeys(gameId));
  for (const [key, value] of Object.entries(remote)) if (!known.has(key)) out[key] = value;

  const mapKey = mapSaveKey(gameId);
  const a = mapOf(gameId, local);
  const b = mapOf(gameId, remote);
  if (a && b) out[mapKey] = mergeProgress(a, b);
  else if (a || b) out[mapKey] = a ?? b;

  const ownKey = ownSaveKey(gameId);
  const mine = local[ownKey];
  const theirs = remote[ownKey];
  const custom = OWN_SAVE_MERGES[gameId];
  if (mine !== undefined && theirs !== undefined) {
    if (custom && isRecord(mine) && isRecord(theirs)) out[ownKey] = custom(mine, theirs);
    else out[ownKey] = reach(gameId, remote) > reach(gameId, local) ? theirs : mine;
  } else if (mine !== undefined || theirs !== undefined) {
    out[ownKey] = mine !== undefined ? mine : theirs;
  }
  return out;
}

/** The game's keys as stored on this device (unreadable values are left out). */
export function readLocalSave(storage: SaveStorage, gameId: string): SaveData {
  const data: SaveData = {};
  for (const key of saveKeys(gameId)) {
    try {
      const raw = storage.getItem(key);
      if (raw !== null) data[key] = JSON.parse(raw) as unknown;
    } catch {
      /* storage disabled or a broken value: leave it out */
    }
  }
  return data;
}

/** Write the game's keys that differ; true when anything on this device changed. Other keys are never touched. */
export function writeLocalSave(storage: SaveStorage, gameId: string, data: SaveData): boolean {
  const before = readLocalSave(storage, gameId);
  let changed = false;
  for (const key of saveKeys(gameId)) {
    if (!(key in data) || sameValue(before[key], data[key])) continue;
    try {
      storage.setItem(key, JSON.stringify(data[key]));
      changed = true;
    } catch {
      /* private mode: the next sync tries again */
    }
  }
  return changed;
}

function canonical(value: unknown): string {
  if (Array.isArray(value)) return `[${value.map(canonical).join(",")}]`;
  if (isRecord(value)) {
    return `{${Object.keys(value)
      .sort()
      .map((key) => `${JSON.stringify(key)}:${canonical(value[key])}`)
      .join(",")}}`;
  }
  return JSON.stringify(value) ?? "null";
}

export function sameValue(a: unknown, b: unknown): boolean {
  return canonical(a) === canonical(b);
}

/** Nothing worth keeping yet: no own save, and a road still at level 1 without stars or skips (a first map open). */
function isFresh(gameId: string, data: SaveData): boolean {
  if (Object.keys(data).some((key) => key !== mapSaveKey(gameId))) return false;
  const map = progressOf(data[mapSaveKey(gameId)]);
  return !map || (map.unlocked <= 1 && !Object.keys(map.stars).length && !Object.keys(map.skipped).length);
}

export interface SyncResult {
  /** "offline" when the saves could not be read or a write did not land; the next sync tries again. */
  status: "synced" | "offline";
  /** Games whose saves on this device changed (the map redraws). */
  changed: string[];
}

/**
 * One round for each game: read the account's saves, merge with this device's, write the result here and there.
 * A stale write (409) is merged with the copy the server returns and retried once.
 */
export async function syncSaves(client: SaveClient, storage: SaveStorage, gameIds: readonly GameId[]): Promise<SyncResult> {
  const saves = await client.getSaves();
  if (!saves) return { status: "offline", changed: [] };
  const byGame = new Map<string, SaveRecord>();
  for (const save of saves) byGame.set(save.gameId, save);
  const changed: string[] = [];
  let landed = true;
  for (const gameId of gameIds) {
    let remote = byGame.get(gameId);
    // Read this device's copy only now: the game may have written while the saves were on their way.
    let merged = mergeSaves(gameId, readLocalSave(storage, gameId), remote?.data ?? {});
    if (!remote && isFresh(gameId, merged)) continue;
    if (writeLocalSave(storage, gameId, merged) && !changed.includes(gameId)) changed.push(gameId);
    for (let attempt = 0; attempt < 2; attempt++) {
      if (remote && sameValue(merged, remote.data)) break;
      const result = await client.putSave(gameId, merged, remote?.version ?? 0);
      if (result.ok) break;
      if (!result.conflict || attempt === 1) {
        landed = false;
        break;
      }
      remote = result.conflict;
      merged = mergeSaves(gameId, readLocalSave(storage, gameId), remote.data);
      if (writeLocalSave(storage, gameId, merged) && !changed.includes(gameId)) changed.push(gameId);
    }
  }
  return { status: landed ? "synced" : "offline", changed };
}
